import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Loader2 } from "lucide-react";
import {
  RegistrationGroup,
  InvoiceLineItem,
} from "../../api/getEventTransactionDetails/types";
import { initiatePaymentRefund } from "../../api/initiatePaymentRefund/initiatePaymentRefundApi";
import { RefundLineItem } from "../../api/initiatePaymentRefund/types";
import { useToast } from "@/hooks/useToast";

interface RefundPanelProps {
  group: RegistrationGroup;
  onCancel: () => void;
  onSuccess: () => void;
}

interface SelectedLineItem {
  lineItemId: string;
  lineItem: InvoiceLineItem;
  quantity: number;
}

export function RefundPanel({ group, onCancel, onSuccess }: RefundPanelProps) {
  const { toast } = useToast();
  const [selectedLineItems, setSelectedLineItems] = useState<
    Map<string, SelectedLineItem>
  >(new Map());
  const [reason, setReason] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [notifyAttendee, setNotifyAttendee] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const handleLineItemToggle = (
    lineItem: InvoiceLineItem,
    checked: boolean,
  ) => {
    const newSelected = new Map(selectedLineItems);

    if (checked) {
      // Add with max refundable quantity
      newSelected.set(lineItem.id, {
        lineItemId: lineItem.id,
        lineItem,
        quantity: lineItem.maxRefundableQuantity,
      });
    } else {
      newSelected.delete(lineItem.id);
    }

    setSelectedLineItems(newSelected);
    setError(null);
  };

  const handleQuantityChange = (lineItemId: string, newQuantity: number) => {
    const selected = selectedLineItems.get(lineItemId);
    if (!selected) return;

    const newSelected = new Map(selectedLineItems);
    newSelected.set(lineItemId, {
      ...selected,
      quantity: Math.max(
        0,
        Math.min(newQuantity, selected.lineItem.maxRefundableQuantity),
      ),
    });
    setSelectedLineItems(newSelected);
  };

  const calculateTotalRefund = () => {
    let total = 0;
    selectedLineItems.forEach((selected) => {
      total += selected.lineItem.unitPrice * selected.quantity;
    });
    return total;
  };

  const handleSubmit = async () => {
    setError(null);

    if (selectedLineItems.size === 0) {
      setError("Please select at least one line item to refund");
      return;
    }

    if (!reason.trim()) {
      setError("Please provide a reason for the refund");
      return;
    }

    const totalRefund = calculateTotalRefund();
    if (totalRefund <= 0) {
      setError("Refund amount must be greater than $0");
      return;
    }

    try {
      setIsSubmitting(true);

      // Get the first invoice (for now, we assume single invoice per group)
      const invoice = group.invoices[0];
      if (!invoice) {
        throw new Error("No invoice found for this transaction");
      }

      // Build line items payload
      const lineItems: RefundLineItem[] = Array.from(
        selectedLineItems.values(),
      ).map((selected) => ({
        lineItemUuid: selected.lineItem.id,
        quantity: selected.quantity,
      }));

      await initiatePaymentRefund({
        uuid: invoice.id,
        lineItems,
        reason: reason.trim(),
        internalNotes: internalNotes.trim() || null,
        notifyAttendee,
      });

      toast({
        title: "Refund initiated",
        description: `Refund of ${formatCurrency(totalRefund)} has been initiated successfully`,
      });

      onSuccess();
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to initiate refund",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get all refundable line items from all invoices
  const refundableLineItems: InvoiceLineItem[] = [];
  group.invoices.forEach((invoice) => {
    invoice.lineItems.forEach((lineItem) => {
      if (lineItem.maxRefundableQuantity > 0) {
        refundableLineItems.push(lineItem);
      }
    });
  });

  const totalRefund = calculateTotalRefund();
  const hasSelection = selectedLineItems.size > 0;

  return (
    <div className="rounded-lg border p-4 bg-muted/30">
      <div className="space-y-4">
        <div>
          <h4 className="text-sm font-medium mb-3">
            Select Line Items to Refund
          </h4>

          {refundableLineItems.length === 0 ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No refundable line items available
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-2">
              {refundableLineItems.map((lineItem) => {
                const selected = selectedLineItems.get(lineItem.id);
                const isChecked = !!selected;
                const maxRefundAmount =
                  lineItem.unitPrice * lineItem.maxRefundableQuantity;

                return (
                  <div
                    className="flex items-start gap-3 rounded-lg border p-3 bg-background"
                    key={lineItem.id}
                  >
                    <Checkbox
                      checked={isChecked}
                      disabled={lineItem.isFullyRefunded}
                      onCheckedChange={(checked) =>
                        handleLineItemToggle(lineItem, checked as boolean)
                      }
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium">
                        {lineItem.description}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {formatCurrency(lineItem.unitPrice)} × max{" "}
                        {lineItem.maxRefundableQuantity.toFixed(2)} qty ={" "}
                        {formatCurrency(maxRefundAmount)}
                      </div>
                      {lineItem.totalRefundedQuantity > 0 && (
                        <div className="text-xs text-muted-foreground mt-1">
                          Already refunded:{" "}
                          {lineItem.totalRefundedQuantity.toFixed(2)} qty (
                          {formatCurrency(lineItem.totalRefundedAmount)})
                        </div>
                      )}
                      {isChecked && (
                        <div className="mt-2 flex items-center gap-2">
                          <Label
                            className="text-xs"
                            htmlFor={`qty-${lineItem.id}`}
                          >
                            Quantity:
                          </Label>
                          <input
                            className="w-24 rounded-md border border-input bg-background px-3 py-1 text-sm"
                            id={`qty-${lineItem.id}`}
                            max={lineItem.maxRefundableQuantity}
                            min="0"
                            onChange={(e) =>
                              handleQuantityChange(
                                lineItem.id,
                                parseFloat(e.target.value) || 0,
                              )
                            }
                            step="0.01"
                            type="number"
                            value={selected?.quantity || 0}
                          />
                          <span className="text-xs text-muted-foreground">
                            (max: {lineItem.maxRefundableQuantity.toFixed(2)})
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="text-sm font-medium">
                      {isChecked && selected
                        ? formatCurrency(lineItem.unitPrice * selected.quantity)
                        : formatCurrency(maxRefundAmount)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {hasSelection && (
          <>
            <Separator />

            <div className="flex justify-between items-center text-sm font-medium">
              <span>Total Refund Amount</span>
              <span className="text-lg">{formatCurrency(totalRefund)}</span>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-sm" htmlFor="reason">
                  Reason for Refund *
                </Label>
                <Textarea
                  className="mt-1.5"
                  id="reason"
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Enter the reason for this refund..."
                  rows={3}
                  value={reason}
                />
              </div>

              <div>
                <Label className="text-sm" htmlFor="internalNotes">
                  Internal Notes (Optional)
                </Label>
                <Textarea
                  className="mt-1.5"
                  id="internalNotes"
                  onChange={(e) => setInternalNotes(e.target.value)}
                  placeholder="Add any internal notes..."
                  rows={2}
                  value={internalNotes}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={notifyAttendee}
                  id="notifyAttendee"
                  onCheckedChange={(checked) =>
                    setNotifyAttendee(checked as boolean)
                  }
                />
                <Label
                  className="text-sm font-normal cursor-pointer"
                  htmlFor="notifyAttendee"
                >
                  Notify attendee of this refund
                </Label>
              </div>
            </div>
          </>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button disabled={isSubmitting} onClick={onCancel} variant="outline">
            Cancel
          </Button>
          <Button
            disabled={isSubmitting || !hasSelection || !reason.trim()}
            onClick={handleSubmit}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Process Refund
          </Button>
        </div>
      </div>
    </div>
  );
}
